import { GetStaticProps } from 'next';
import React from 'react';

import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import { getServerPageGetSubsidyOverview } from 'api/graphql-helper';
import { PageInformationFieldsFragment, SubsidyOverviewSingleFieldsFragment, Theme } from 'api/graphql-types';

import { Head } from 'modules/head/head';
import IntroHeader from 'modules/intro-header/intro-header';
import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import { initialState as subsidyInitialState } from 'redux/stores/subsidy';
import { initialState as groupsInitialState } from 'redux/stores/groups';
import { initialState as kindsInitialState } from 'redux/stores/kinds';
import { initialState as scalesInitialState } from 'redux/stores/scales';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { initialState as settingsInitialState } from 'redux/stores/settings';
import { IBasePageProps } from 'types/page';
import { ROUTES } from 'constants/routes';
import { searchSubsidy } from 'api/algolia';
import { SUBSIDY_OVERVIEW_LIMIT } from 'modules/subsidy-overview/subsidy-overview';

interface ISubsidyOverviewPageProps extends IBasePageProps {
	subsidyOverview?: any;
	pageInformation?: any;
}

const SubsidyOverviewPage = (props: ISubsidyOverviewPageProps): React.JSX.Element => {
	const modules = props.subsidyOverview?.modules;
	const seoModule = props?.subsidyOverview?.seo;

	return (
		<Layout navbar={props?.pageInformation?.navbar} footer={props?.pageInformation?.footer}>
			<Head seoModule={seoModule} seoParent={props?.pageInformation?.seo} />
			<IntroHeader module={props?.subsidyOverview?.header} slug={ROUTES.SUBSIDIES.fullPath} />
			<ModuleBuilder modules={modules} slug={ROUTES.SUBSIDIES.fullPath} />
		</Layout>
	);
};

export const getStaticProps: GetStaticProps<ISubsidyOverviewPageProps> = async (ctx) => {
	try {
		const { locale, publicationState, draftMode } = getStrapiParametersFromContext(ctx);

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const result = await getServerPageGetSubsidyOverview(
			{
				variables: {
					locale,
					publicationState
				}
			},
			client
		);

		const { subsidyOverview, pageInformation, themes_connection: themes, groups, kinds, scales } = result.props.data;

		// Gracefully handle Algolia failures - don't let search failures prevent page build
		let subsidy: any[] = [];
		let subsidyCount = 0;

		try {
			const searchResult = await searchSubsidy(
				'',
				{
					status: 'open',
					subsidyKindsIds: [],
					subsidyScalesIds: [],
					targetGroupsIds: [],
					themesIds: []
				},
				{
					sort: 'asc',
					page: 1,
					limit: SUBSIDY_OVERVIEW_LIMIT,
					locale
				}
			);
			subsidy = searchResult.results;
			subsidyCount = searchResult.total;
		} catch (algoliaError: any) {
			// eslint-disable-next-line no-console
			console.error('Algolia search failed, using empty results as fallback:', {
				message: algoliaError?.message,
				error: algoliaError
			});
			// Continue with empty results - client-side can still fetch data
		}

		if (subsidyOverview == null) {
			return {
				notFound: true
			};
		}

		return {
			props: {
				pageInformation: pageInformation?.data?.attributes as PageInformationFieldsFragment,
				subsidyOverview: subsidyOverview.data?.attributes as SubsidyOverviewSingleFieldsFragment,
				initialReduxState: {
					settings: {
						...settingsInitialState,
						publicationState,
						draftMode,
						contentType: 'subsidy-overview',
						contentTypeId: subsidyOverview?.data?.documentId
					},
					subsidy: {
						...subsidyInitialState,
						subsidyCount,
						subsidy
					},
					groups: {
						...groupsInitialState,
						groups: groups?.data?.map((item) => ({
							id: item?.documentId,
							locale,
							name: item?.attributes?.name
						})),
						totalGroups: groups?.pageInfo.total || 0
					},
					kinds: {
						...kindsInitialState,
						kinds: kinds?.data?.map((item) => ({
							id: item?.documentId,
							locale,
							name: item?.attributes?.name
						})),
						totalKinds: kinds?.pageInfo.total || 0
					},
					scales: {
						...scalesInitialState,
						scales: scales?.data?.map((item) => ({
							id: item?.documentId,
							locale,
							name: item?.attributes?.name
						})),
						totalScales: scales?.pageInfo.total || 0
					},
					themes: {
						themes: (themes?.data as Theme[]) || [],
						totalThemes: themes?.pageInfo.total || 0
					}
				}
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in subsidy overview', {
			message: error?.message,
			stack: error?.stack,
			graphQLErrors: error?.graphQLErrors,
			networkError: error?.networkError,
			fullError: error
		});

		return {
			notFound: true
		};
	}
};

export default SubsidyOverviewPage;
